/**
 * What the block picker offers, how it groups it, and how it is found.
 *
 * Both studios add blocks from the same 13 slide types, so the rows are built
 * from one source — `SLIDE_TYPES_PICKER` — rather than each studio listing them
 * again. That is what stops the two pickers from drifting: a new slide type
 * appears in both, in the section its type declares here.
 *
 * FlashBook adds *presentations* on top: a quote, a note, a captioned figure, a
 * table and a formula are all `content` blocks that arrive knowing their kind
 * (`patch`), so they ride along as extra rows in the section their type belongs
 * to. The list is data, the grouping is data, and "what is a block" stays a
 * property of the slide model rather than of either screen.
 *
 * Sections are how a creator *browses*, and searching is how a creator *reaches*
 * — so the two are answered by the same list. A query is matched against every row
 * of both libraries at once (the thirteen plain types and the rich reading blocks
 * they are presentations of), the answer is handed back flat rather than regrouped
 * (a result belongs to the search, not to a section), and the rows the creator used
 * last lead it (see `#/lib/studioRecentBlocks`).
 */
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {SLIDE_TYPES_PICKER} from '#/lib/slideFactory'
import {type Slide, type SlideType} from '#/types/capsule'

/**
 * A row's searchable text: its label, its description and its key, in the language
 * the creator is reading the sheet in.
 *
 * Built by the sheet, which is the only place a label can be translated (`l`), and
 * handed to the matcher below — so what is searched is exactly what is painted, and
 * the matching itself stays a pure function a unit test can drive without a React
 * tree.
 */
export type StudioPickerRowText = (entry: StudioPickerEntry) => string

/** One row of the picker: a type, or a type plus the presentation it arrives in. */
export type StudioPickerEntry = {
  /** Unique list key: the type, or `content:quote` for a presented variant. */
  key: string
  type: SlideType
  /** Applied on creation, so a rich reading block is never briefly plain. */
  patch?: Partial<Slide>
  label: MessageDescriptor
  desc: MessageDescriptor
  icon: string
  color: string
}

export type StudioPickerSection = {
  key: string
  label: MessageDescriptor
  entries: StudioPickerEntry[]
}

/**
 * How the rows are grouped, in the order a creator fills a book: the reading
 * block first, then questions, then the word-level exercises.
 *
 * Membership is declared by type, so an entry added above lands in the right
 * section without a second decision — and anything a future type forgets to
 * claim still appears, under "More", instead of vanishing from the picker.
 */
const PICKER_GROUPS: {label: MessageDescriptor; types: SlideType[]}[] = [
  {label: msg`Reading`, types: ['content']},
  {
    label: msg`Questions`,
    types: ['single_choice', 'multiple_choice', 'true_false', 'image_choice'],
  },
  {
    label: msg`Words & text`,
    types: [
      'fill_blanks',
      'complex_fill_blank',
      'drag_words',
      'mark_words',
      'find_words',
    ],
  },
  {label: msg`Matching & order`, types: ['matching', 'ordering']},
  {label: msg`Explore`, types: ['accordion']},
]

/** The row metadata for every plain slide type the factory offers. */
export const BASE_PICKER_ENTRIES: StudioPickerEntry[] = SLIDE_TYPES_PICKER.map(
  item => ({
    key: item.type,
    type: item.type,
    label: item.label,
    desc: item.desc,
    icon: item.icon,
    color: item.color,
  }),
)

/**
 * The picker's sections, with `extra` rows (FlashBook's rich reading blocks)
 * slotted in beside the plain type they are a presentation of.
 */
export function buildStudioPickerSections(
  extra: StudioPickerEntry[] = [],
): StudioPickerSection[] {
  const entries = [...BASE_PICKER_ENTRIES, ...extra]
  const claimed = new Set(PICKER_GROUPS.flatMap(group => group.types))
  const unfiled = entries.filter(entry => !claimed.has(entry.type))

  const sections: StudioPickerSection[] = PICKER_GROUPS.map(group => ({
    key: 'types:' + group.types.join(','),
    label: group.label,
    entries: entries.filter(entry => group.types.includes(entry.type)),
  }))
  if (unfiled.length) {
    sections.push({key: 'types:more', label: msg`More`, entries: unfiled})
  }
  return sections.filter(section => section.entries.length > 0)
}

/**
 * Case- and accent-insensitive, so a query typed without a diacritic still finds
 * the row it means ("resume" for "Résumé").
 */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/**
 * Whether a row answers to a query.
 *
 * Every word of the query has to appear somewhere in the row's text, in any order
 * — so "choice image" finds "Image choice", and "table rows" finds the table by
 * its description as happily as "table" does. An empty query matches everything,
 * which is what lets the sheet keep showing its sections until the creator types.
 */
export function studioPickerMatches(query: string, text: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean)
  if (words.length === 0) return true
  const row = fold(text)
  return words.every(word => row.includes(word))
}

/**
 * Every row a query finds, as one flat list.
 *
 * `sections` already carries both libraries — the plain slide types and the rich
 * reading blocks slotted in beside the type they are a presentation of — so a
 * single pass searches all of them, and a word like "image" can answer with a
 * captioned figure and an image question in the one list.
 *
 * Recently used rows come first, in the order they were last used, and the rest
 * keep the order they are grouped in (`sort` is stable). The list is deliberately
 * unlabelled: while there is a query, "which section was this row in" is not the
 * question the creator is asking.
 */
export function searchStudioPickerEntries(
  sections: StudioPickerSection[],
  query: string,
  textOf: StudioPickerRowText,
  recentKeys: readonly string[] = [],
): StudioPickerEntry[] {
  const rank = new Map(recentKeys.map((key, index) => [key, index]))
  return sections
    .flatMap(section => section.entries)
    .filter(entry => studioPickerMatches(query, textOf(entry)))
    .sort(
      (a, b) =>
        (rank.get(a.key) ?? recentKeys.length) -
        (rank.get(b.key) ?? recentKeys.length),
    )
}

/**
 * The rows behind a list of recently used keys.
 *
 * Resolved against `sections` rather than trusted as ids, so the sheet can only
 * offer what it actually has: a block this build no longer carries — or one of the
 * other studio's, in a sheet that does not offer it — drops out instead of
 * rendering a row with nothing behind it. The list's own order is kept (newest
 * first), and a key that appears twice is taken once.
 */
export function recentStudioPickerEntries(
  sections: StudioPickerSection[],
  keys: readonly string[],
): StudioPickerEntry[] {
  const available = new Map<string, StudioPickerEntry>()
  for (const section of sections) {
    for (const entry of section.entries) {
      if (!available.has(entry.key)) available.set(entry.key, entry)
    }
  }
  const recent: StudioPickerEntry[] = []
  for (const key of keys) {
    const entry = available.get(key)
    if (entry && !recent.includes(entry)) recent.push(entry)
  }
  return recent
}
